import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Trophy, Calendar, Clock, Star, ChevronDown, ChevronRight, Dumbbell, ArrowLeft, Share2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTENSITY_COLOR = {
  Easy: 'text-green-400 bg-green-400/10 border-green-400/20',
  Moderate: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
  Hard: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  Max: 'text-red-400 bg-red-400/10 border-red-400/20',
};

const DAY_TYPE_ICON = {
  Run: '\u{1F3C3}',
  'Station Work': '\u{1F3CB}\u{FE0F}',
  Strength: '\u{1F4AA}',
  Recovery: '\u{1F9D8}',
  'Race Sim': '\u{1F3C1}',
  Rest: '\u{1F634}',
};

export default function SharedRacePlanPage() {
  const { shareToken } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/race-plan/shared/${shareToken}`);
        if (!resp.ok) throw new Error('Plan not found or no longer shared');
        setPlan(await resp.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [shareToken]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="shared-plan-loading">
        <Loader2 className="w-8 h-8 text-[#e8ff00] animate-spin" />
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4" data-testid="shared-plan-error">
        <Trophy className="w-12 h-12 text-zinc-700" />
        <h2 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Plan Not Found</h2>
        <p className="text-zinc-500 text-sm">This plan may have been removed or is no longer shared.</p>
        <Link to="/hyrox" className="text-[#e8ff00] text-sm hover:underline flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Go to HYROX Hub
        </Link>
      </div>
    );
  }

  const p = plan.plan || {};

  return (
    <div className="min-h-screen bg-black py-20 px-4" data-testid="shared-plan-page">
      <div className="max-w-3xl mx-auto">
        {/* Back link */}
        <Link to="/hyrox" className="text-zinc-500 text-xs hover:text-zinc-300 flex items-center gap-1 mb-6 transition-colors">
          <ArrowLeft className="w-3 h-3" /> Back to HYROX Hub
        </Link>

        {/* Header */}
        <div className="border border-zinc-800 bg-zinc-900/40 p-6 mb-6" data-testid="shared-plan-header">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[#e8ff00] text-[10px] uppercase tracking-wider mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Shared HYROX Training Plan
              </p>
              <h1 className="text-white text-2xl md:text-3xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                {p.plan_name || 'HYROX Race Plan'}
              </h1>
              <p className="text-zinc-400 text-sm mt-2">{p.summary}</p>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider px-2 py-0.5 border border-zinc-700" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  By {plan.creator_name}
                </span>
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider px-2 py-0.5 border border-zinc-800" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {plan.experience_level || 'intermediate'}
                </span>
                {plan.race_date && (
                  <span className="text-[10px] text-[#e8ff00] flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Race: {plan.race_date}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 text-zinc-400 text-xs hover:border-[#e8ff00] hover:text-[#e8ff00] transition-colors"
              data-testid="copy-share-link-btn"
            >
              <Share2 className="w-3.5 h-3.5" />
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>

        {/* Weekly Mileage */}
        {p.weekly_mileage_progression && (
          <div className="border border-zinc-800 bg-zinc-900/30 p-4 mb-6">
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Weekly Volume</p>
            <div className="flex items-end gap-1 h-16">
              {p.weekly_mileage_progression.map((m, i) => (
                <div key={`week-${i + 1}`} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full bg-[#e8ff00]/20 border border-[#e8ff00]/30 transition-all"
                    style={{ height: `${(m / Math.max(...p.weekly_mileage_progression)) * 48}px` }}
                  />
                  <span className="text-[9px] text-zinc-600">W{i + 1}</span>
                </div>
              ))}
              <span className="text-[9px] text-zinc-600 ml-1 self-center">km/wk</span>
            </div>
          </div>
        )}

        {/* Weeks */}
        <div className="space-y-2 mb-6">
          {(p.weeks || []).map((week) => (
            <div key={week.week} className="border border-zinc-800 bg-zinc-900/30 overflow-hidden" data-testid={`shared-week-${week.week}`}>
              <button
                onClick={() => setExpandedWeek(expandedWeek === week.week ? null : week.week)}
                className="w-full flex items-center justify-between px-4 py-3 text-left group hover:bg-zinc-900/60 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-[#e8ff00] text-lg font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>W{week.week}</span>
                  <div>
                    <p className="text-white text-sm font-bold uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{week.theme}</p>
                    <p className="text-zinc-500 text-xs">{week.focus}</p>
                  </div>
                </div>
                {expandedWeek === week.week ? <ChevronDown className="w-4 h-4 text-[#e8ff00]" /> : <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400" />}
              </button>

              {expandedWeek === week.week && (
                <div className="px-4 pb-4 space-y-2 border-t border-zinc-800/50">
                  {(week.days || []).map((day, di) => (
                    <div key={day.day || `day-${di + 1}`} className="flex items-start gap-3 py-2.5 border-b border-zinc-800/30 last:border-0">
                      <div className="w-20 flex-shrink-0">
                        <p className="text-zinc-500 text-xs font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{day.day}</p>
                        <p className="text-zinc-700 text-[10px]">{DAY_TYPE_ICON[day.type] || '\u{1F4CB}'} {day.type}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{day.title}</p>
                        <p className="text-zinc-500 text-xs mt-0.5 leading-relaxed">{day.description}</p>
                        {day.stations_targeted && day.stations_targeted.length > 0 && (
                          <div className="flex gap-1 mt-1.5 flex-wrap">
                            {day.stations_targeted.map((s) => (
                              <span key={s} className="text-[9px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 border border-zinc-700">{s}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-2">
                        {day.duration_min && (
                          <span className="text-zinc-600 text-[10px] flex items-center gap-0.5">
                            <Clock className="w-3 h-3" /> {day.duration_min}m
                          </span>
                        )}
                        {day.intensity && (
                          <span className={`text-[10px] px-1.5 py-0.5 border ${INTENSITY_COLOR[day.intensity] || 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}>
                            {day.intensity}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Tips */}
        {p.tips && p.tips.length > 0 && (
          <div className="bg-[#e8ff00]/5 border border-[#e8ff00]/20 p-4 mb-8">
            <p className="text-[#e8ff00] text-xs uppercase tracking-wider mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Coach's Tips</p>
            {p.tips.map((tip) => (
              <p key={tip} className="text-zinc-400 text-sm flex items-start gap-2 mb-1.5">
                <Star className="w-3 h-3 text-[#e8ff00] mt-1 flex-shrink-0" /> {tip}
              </p>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="text-center border border-zinc-800 bg-zinc-900/40 p-8">
          <Dumbbell className="w-8 h-8 text-[#e8ff00] mx-auto mb-3" />
          <h3 className="text-white text-lg mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Want Your Own Personalized Plan?</h3>
          <p className="text-zinc-500 text-sm mb-4">Generate a custom 8-week HYROX training plan tailored to your fitness level</p>
          <Link
            to="/hyrox#race-plan"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#e8ff00] text-black text-xs uppercase tracking-wider hover:opacity-90 transition-opacity"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="create-own-plan-cta"
          >
            <Trophy className="w-4 h-4" /> Create My Plan
          </Link>
        </div>
      </div>
    </div>
  );
}
